/**
 * Month-range presets for the Co-op Counts screen (task 007-03).
 *
 * Re-implementation of the module-local helpers in `screens/sites.tsx`
 * (Publisher uniques panel), exported so the screen and its tests share one
 * definition. Kept byte-for-byte in behaviour with `sites.tsx` on purpose.
 *
 * Known, accepted (task 007-03 §Scope 2): the range is computed in the
 * browser's local zone while the api bounds the query in `DISTRIBUTION_TZ`
 * (America/New_York), so on a machine set to another zone a preset can pick a
 * boundary day the api treats as the adjacent day. Not fixed here — the
 * sites.tsx behaviour is the baseline.
 */

export type Preset = 'today' | 'this_month' | 'last_month' | 'custom';

/**
 * The zone the api buckets reporting days in — mirrors the api's
 * `DISTRIBUTION_TZ`. Unlike the month presets (browser zone, accepted above),
 * the Today preset uses it: for a one-day range the browser-zone mismatch is
 * no longer an edge case — in the evening, a browser west of ET would ask for
 * the wrong day as soon as ET passes midnight (task 087-01 D1, QA-34).
 */
export const REPORTING_TZ = 'America/New_York';

// `en-CA` formats a date as `YYYY-MM-DD`.
const REPORTING_DAY = new Intl.DateTimeFormat('en-CA', { timeZone: REPORTING_TZ });

/** The calendar day of `now` in `REPORTING_TZ`, `YYYY-MM-DD`. */
export function todayEtYmd(now = new Date()): string {
  return REPORTING_DAY.format(now);
}

/** The Today preset: the current `REPORTING_TZ` day, as a one-day range. */
export function todayRange(): [string, string] {
  const today = todayEtYmd();
  return [today, today];
}

/** Today in the browser's local zone, `YYYY-MM-DD`. */
export function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** First of the current month → today, inclusive. */
export function thisMonthRange(): [string, string] {
  const today = todayYmd();
  return [`${today.slice(0, 7)}-01`, today];
}

/** First → last day of the previous calendar month (local zone). */
export function lastMonthRange(): [string, string] {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const last = new Date(d.getFullYear(), d.getMonth(), 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (date: Date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return [fmt(first), fmt(last)];
}
